import React, {useState, useEffect, useCallback, useRef} from "react";
import {createPortal} from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch, faEllipsisVertical} from "@fortawesome/free-solid-svg-icons";

// Utils
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Components
import TableReportRecords from "../reports/TableReportRecords.js";
import TableReportSharepoint from "../reports/TableReportSharepoint.js";
import ModalArchive from "../reports/ModalArchive.js";
import ModalDelete from "../../components/ModalDelete.js";

// Style
import {pad, border, radius, transition} from "../../style/components/variables.js";
import {
  Text,
  Loader,
  NotLoaded,
  Button,
  SearchWrapper,
  Search,
  SearchIcon
} from "../../style/components/general.js";
import {flex, z} from "../../style/components/mixins.js";

const Reports = ({facilityId, actionMenuEl}) => {
  const isMounted = useMountedState();

  const {data, loading, api} = useApi("reports");

  const [currentReport, setCurrentReport] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [initialLoad, setInitialLoad] = useState(true);

  const [tableQuery, setTableQuery] = useState(false);
  const [triggerToggleAll, setTriggerToggleAll] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const tablesOpen = useRef({});
  const actionsMenuRef = useRef(null);

  const handledOpen = useRef(false);

  const displayReports = data?.filter(
    ({name}) => !tableQuery || name.toLowerCase().includes(tableQuery.toLowerCase())
  );

  const scrollToTable = useCallback(() => {
    let hash = "";
    try {
      hash = window.location.hash;
    } catch {
      return;
    }

    if (!hash) return;

    const tableId = hash.replace("#", "");
    const table = document.getElementById(tableId);
    if (table) {
      const y = table.getBoundingClientRect().top - 100;
      setTimeout(() => {
        try {
          window.scrollBy({behavior: "smooth", top: y});
        } catch {
          /* ignore */
        }
      }, 500);
    }
    setInitialLoad(false);
  }, []);

  const getReports = useCallback(() => {
    api
      .callGet(null, {
        facilityId,
        orderBy: "asc",
        groupBy: "frequency"
      })
      .then(response => {
        if (isMounted() && response?.status === 200) scrollToTable();
      });
  }, [api, facilityId, scrollToTable, isMounted]);

  useEffect(() => {
    if (isMounted() && facilityId && initialLoad) getReports();
  }, [api, facilityId, initialLoad, isMounted, getReports]);

  const openArchive = report => {
    setCurrentReport(report);
    setShowArchive(true);
  };

  const openDelete = report => {
    setCurrentReport(report);
    setShowDelete(true);
  };

  const archiveReport = () =>
    api.callPut(currentReport.id, {archive: true}).then(() => {
      getReports();
      setShowArchive(false);
    });

  const publishReport = report => api.callPut(report.id, {draft: false}).then(() => getReports());

  const deleteReport = () =>
    api.callDelete(currentReport.id).then(() => {
      getReports();
      setShowDelete(false);
    });

  const restoreReport = report => api.callPut(report.id, {archive: false}).then(() => getReports());

  const onOpen = useCallback(
    (state, reportId) => {
      tablesOpen.current[reportId] = state;
      if (displayReports?.every(({id}) => tablesOpen.current[id])) {
        setAllOpen(true);
      } else setAllOpen(false);
      setTriggerToggleAll(false);
    },
    [displayReports]
  );

  useEffect(() => {
    if (displayReports?.every(({id}) => tablesOpen.current[id])) {
      setAllOpen(true);
    } else setAllOpen(false);
    setTriggerToggleAll(false);
  }, [displayReports, tableQuery]);

  const handleTableSearch = e => {
    setTableQuery(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    const handleEscape = e => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showActionsMenu]);

  const noReports = !displayReports || displayReports.length === 0;

  let emptyReportsMessage = "No reports found.";
  if (tableQuery && data?.length > 0) emptyReportsMessage = "No reports match your search.";
  else if (!data || data.length === 0)
    emptyReportsMessage = "No Reports have been created for this facility.";

  const actionsMenu = (
    <ActionsMenuWrapper ref={actionsMenuRef}>
      <ActionMenuButton
        title="More"
        type="button"
        $active={!!tableQuery || showActionsMenu}
        aria-label="Report actions"
        aria-expanded={showActionsMenu}
        aria-haspopup="true"
        data-testid="reports.moreActions"
        onClick={() => setShowActionsMenu(prev => !prev)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </ActionMenuButton>
      {showActionsMenu && (
        <ActionsMenu>
          <MenuSearch>
            <SearchWrapper>
              <SearchIcon>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
              <StyledSearch
                type="text"
                name="search"
                placeholder="Reports..."
                autoFocus
                value={tableQuery || ""}
                onChange={handleTableSearch}
              />
            </SearchWrapper>
          </MenuSearch>
          {!noReports && (
            <ActionItem
              type="button"
              title={allOpen ? "Collapse All Tables" : "Expand All Tables"}
              onClick={() => {
                setTriggerToggleAll(true);
                setShowActionsMenu(false);
              }}>
              {allOpen ? "Collapse All Tables" : "Expand All Tables"}
            </ActionItem>
          )}
        </ActionsMenu>
      )}
    </ActionsMenuWrapper>
  );

  return (
    <>
      {actionMenuEl
        ? createPortal(actionsMenu, actionMenuEl)
        : !loading && (!noReports || tableQuery) && <InlineMenu>{actionsMenu}</InlineMenu>}

      {loading ? (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      ) : (
        <OutermostWrapper>
          {noReports && (
            <NotFound>
              <Text>{emptyReportsMessage}</Text>
            </NotFound>
          )}
          {displayReports?.map((report, idx) => (
            <div id={`r${report.id}`} key={report.id}>
              {report.sharepointFolderId ? (
                <TableReportSharepoint
                  index={idx + 1}
                  report={report}
                  showDelete={openDelete}
                  archiveReport={openArchive}
                  restoreReport={restoreReport}
                  publishReport={publishReport}
                  onOpen={onOpen}
                  triggerOpen={triggerToggleAll ? !allOpen : undefined}
                  handledOpen={handledOpen}
                />
              ) : (
                <TableReportRecords
                  index={idx + 1}
                  report={report}
                  showDelete={openDelete}
                  archiveReport={openArchive}
                  restoreReport={restoreReport}
                  publishReport={publishReport}
                  onOpen={onOpen}
                  triggerOpen={triggerToggleAll ? !allOpen : undefined}
                  handledOpen={handledOpen}
                />
              )}
            </div>
          ))}
        </OutermostWrapper>
      )}

      {showArchive && (
        <ModalArchive
          visible={showArchive}
          setVisible={setShowArchive}
          archive={archiveReport}
          loading={loading}
        />
      )}

      {showDelete && (
        <ModalDelete
          visible={showDelete}
          setVisible={setShowDelete}
          confirmDelete={deleteReport}
          title="Delete Report"
          loading={loading}
        />
      )}
    </>
  );
};

Reports.propTypes = {
  facilityId: PropTypes.number.isRequired,
  actionMenuEl: PropTypes.object
};

// Style Overrides
const OutermostWrapper = styled.div`
  ${flex("column", "nowrap")}

  margin-bottom: ${pad * 2}px;
`;

const InlineMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${pad}px;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const ActionMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 36px;
  min-height: 32px;
  padding-left: ${pad}px;
  color: ${({theme}) => theme.secondary};
  background-color: transparent;
  transition: ${transition};

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  &:hover {
    background-color: ${({theme}) => theme.secondary};
    color: ${({theme}) => theme.tertiary};

    svg {
      fill: ${({theme}) => theme.tertiary};
    }
  }

  ${({$active, theme}) =>
    $active &&
    `
      background-color: ${theme.secondary};
      color: ${theme.tertiary};

      svg {
        fill: ${theme.tertiary};
      }
    `}
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;
`;

const MenuSearch = styled.div`
  padding: ${pad / 2}px;

  ${SearchWrapper} {
    width: 100%;
  }

  ${SearchIcon} {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme}) => theme.secondary};
  text-align: left;

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
  }
`;

const StyledSearch = styled(Search)`
  padding-left: 30px;

  &[type="text"] {
    max-width: 100%;
    width: 100%;
  }
`;

const NotFound = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 85%;
  min-height: 75px;
  padding: ${pad}px;
  margin-right: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${props => props.theme.secondary};
  width: 100%;
`;

export default Reports;
